import { useState } from "preact/hooks";
import { tr } from "../i18n.js";
import { DAYS, get, html, navigate, post, put, textToWeeklyHours, weeklyHoursToText } from "../lib.js";
import { ErrorBox, Field, Loading, Logo, PageHeader, toast, useAction, useForm, useLoad } from "../ui.js";
import { PageGuide } from "../guide.js";

// Guided setup: the three screens a new business goes through on its first sign-in.
// 1. the owner answers a short intake (half of it already filled from their purchase),
// 2. Claude drafts the agent, services, hours and FAQs and the owner edits the draft,
// 3. applying it writes everything and turns the agent on.

const STEPS = ["Your business", "Review the draft", "Go live"];

function Steps({ step }) {
  return html`<ol class="wizard-steps">
    ${STEPS.map((label, i) => html`<li class=${i === step ? "active" : i < step ? "done" : ""}><span class="n">${i + 1}</span>${tr(label)}</li>`)}
  </ol>`;
}

export function SetupPage({ t }) {
  const [data, reload, error] = useLoad(() => get(`${t.base}/setup`), [t.id]);
  const [step, setStep] = useState(null);
  const [draft, setDraft] = useState(null);
  const [draftsLeft, setDraftsLeft] = useState(null);
  const [siteRequested, setSiteRequested] = useState(false);

  if (error) return html`<${ErrorBox} error=${error} />`;
  if (!data) return html`<${Loading} />`;
  const current = step ?? (data.status === "applied" ? 2 : data.draft ? 1 : 0);
  const active = draft ?? data.draft;
  // The count moves with each draft, so the review screen shows what is really left.
  const view = { ...data, drafts_left: draftsLeft ?? data.drafts_left };

  return html`<div class="wizard">
    <${PageHeader}
      title=${tr("Set up your AI agent")}
      subtitle=${tr("Answer a few questions once. We write the agent's instructions, services, hours and FAQs from them, and you edit anything before it goes live.")}
    />
    <${PageGuide} id="setup" />
    <${Steps} step=${current} />
    ${current === 0 &&
    html`<${IntakeStep}
      t=${t}
      data=${view}
      onDrafted=${(d, left) => {
        setDraft(d);
        setDraftsLeft(left);
        setStep(1);
      }}
      onSkipped=${() => {
        t.reload();
        navigate("/agent");
      }}
    />`}
    ${current === 1 && active &&
    html`<${ReviewStep}
      t=${t}
      data=${view}
      draft=${active}
      onBack=${() => setStep(0)}
      onRedraft=${(d, left) => {
        setDraft(d);
        setDraftsLeft(left);
      }}
      onApplied=${(res) => {
        t.reload();
        setSiteRequested(!!res?.site_requested);
        setStep(2);
      }}
    />`}
    ${current === 1 && !active && html`<div class="card"><p>${tr("The draft is gone. Go back to your answers and draft it again.")}</p><button class="btn" onClick=${() => setStep(0)}>${tr("Back to my answers")}</button></div>`}
    ${current === 2 && html`<${DoneStep} siteRequested=${siteRequested || !!data.intake.wants_site} />`}
  </div>`;
}

/** Step 1: the intake. Everything the purchase page already knew is filled in. */
/** Intake fields "Explore my website" can fill, with the labels used for them on this screen. */
const EXPLORE_LABELS = {
  category: "Kind of business",
  about: "What you do, and for whom",
  services_text: "What customers can book",
  hours_text: "Opening hours",
  policies_text: "Policies",
  faq_text: "What customers ask you most",
  address: "Address and contact details",
  tone: "Tone of voice",
  language: "Main language",
};

function IntakeStep({ t, data, onDrafted, onSkipped }) {
  const prefilledHours = defaultHoursText(data.prefill.weekly_hours);
  const { values, bind, setValues } = useForm({
    ...data.intake,
    // The server already shows the business's current name here, and keeps business_name_from (sent
    // back as it came) so applying renames only when the owner typed a different name in this field.
    business_name: data.intake.business_name || data.prefill.business_name,
    business_name_from: data.intake.business_name_from || data.prefill.business_name,
    agent_name: data.intake.agent_name || `${data.prefill.business_name} Assistant`,
    language: data.intake.language || data.prefill.language,
    hours_text: data.intake.hours_text || prefilledHours,
  });
  const [busy, run] = useAction();
  const [exploring, setExploring] = useState(false);
  const [explored, setExplored] = useState(null);
  const [exploresLeft, setExploresLeft] = useState(data.explores_left);

  // Fills only what the owner hasn't answered (the suggested hours and the default category and
  // language count as unanswered); what they wrote is kept and offered as "use the site's version".
  const isUnanswered = (key) => {
    const v = (values[key] ?? "").trim();
    if (!v) return true;
    if (key === "hours_text") return v === prefilledHours.trim();
    if (key === "category") return v === "other";
    if (key === "language") return v === data.prefill.language;
    return false;
  };
  const explore = async () => {
    setExploring(true);
    try {
      const res = await run(() => post(`${t.base}/setup/explore`, { website_url: values.website_url }));
      const filled = {};
      const kept = {};
      for (const [key, value] of Object.entries(res.suggestions)) {
        if (key === "category_other" || !value) continue;
        if (isUnanswered(key)) filled[key] = value;
        else if ((values[key] ?? "").trim() !== value.trim()) kept[key] = value;
      }
      if (filled.category === "other" && res.suggestions.category_other) filled.category_other = res.suggestions.category_other;
      setValues((v) => ({ ...v, ...filled }));
      setExplored({ filled: Object.keys(filled).filter((k) => EXPLORE_LABELS[k]), kept, pages: res.source.pages.length, url: res.source.url });
      setExploresLeft(res.explores_left);
    } finally {
      setExploring(false);
    }
  };
  const useSiteVersion = () => {
    setValues((v) => ({ ...v, ...explored.kept }));
    setExplored({ ...explored, filled: [...explored.filled, ...Object.keys(explored.kept)], kept: {} });
  };
  const payload = () => ({ ...values, business_name: values.business_name.trim() });

  const draftNow = async (e) => {
    e.preventDefault();
    const res = await run(() => post(`${t.base}/setup/draft`, payload()), tr("Your agent is drafted"));
    onDrafted(res.draft, res.drafts_left);
  };
  const saveForLater = () => run(() => put(`${t.base}/setup/intake`, payload()), tr("Saved. Come back any time."));
  const skip = async () => {
    await run(() => post(`${t.base}/setup/skip`), tr("You can set the agent up yourself under AI agent."));
    onSkipped();
  };

  return html`<form class="card wizard-card" onSubmit=${draftNow}>
    <div class="callout small">
      <strong>${tr("Have a website? Start from it.")}</strong>
      <p class="muted">${tr("We read your site and fill in the answers below from it. You check and edit them before anything is drafted.")}</p>
      <div class="row">
        <${Field} label=${tr("Your website or Instagram page")}>
          <input dir="ltr" placeholder="https://" maxlength="1500" ...${bind("website_url")} />
        </${Field}>
        <p class="muted small">${tr("Up to 3 links, separated by spaces: your site plus a profile such as MedReviews or Google.")}</p>
      </div>
      <div class="form-actions">
        <button type="button" class="btn" disabled=${busy || !values.website_url?.trim() || exploresLeft === 0} onClick=${explore}>
          ${exploring ? tr("Reading your website… this takes about half a minute") : tr("Explore my website")}
        </button>
        ${exploresLeft < 3 && html`<span class="muted small">${tr("{n} website reads left", { n: exploresLeft })}</span>`}
      </div>
      <label class="toggle small">
        <input type="checkbox" checked=${!!values.wants_site} onChange=${(e) => setValues((v) => ({ ...v, wants_site: e.target.checked }))} />
        ${values.website_url?.trim()
          ? tr("Also make me a page at a {domain} address from my website's content, with booking and chat built in. It's my site, and I'll check the page before it goes live.", { domain: "relayza.company" })
          : tr("No website? Build me one for free, at a {domain} address. I'll check it before it goes live.", { domain: "relayza.company" })}
      </label>
      ${explored && html`<div class="callout ok small">
        ${explored.filled.length
          ? tr("Filled {fields} from {n} pages of {url}. Check them below.", { fields: explored.filled.map((k) => tr(EXPLORE_LABELS[k])).join(", "), n: explored.pages, url: explored.url })
          : tr("Your site didn't add anything to what you already wrote.")}
        ${Object.keys(explored.kept).length > 0 && html`<div>
          ${tr("Kept your own answers in: {fields}.", { fields: Object.keys(explored.kept).map((k) => tr(EXPLORE_LABELS[k] ?? k)).join(", ") })}
          <button type="button" class="btn small ghost" onClick=${useSiteVersion}>${tr("Use the website's version instead")}</button>
        </div>`}
      </div>`}
    </div>
    <p class="muted small">${tr("Write the way you'd explain it to a new receptionist. Short answers are fine, and anything you leave out comes back as a question to fill in.")}</p>
    <div class="row">
      <${Field} label=${tr("Business name")}><input required maxlength="100" ...${bind("business_name")} /></${Field}>
      <${Field} label=${tr("Kind of business")}>
        <select ...${bind("category")}>${data.prefill.categories.map((c) => html`<option value=${c.id}>${tr(c.label)}</option>`)}</select>
      </${Field}>
    </div>
    ${values.category === "other" && html`<${Field} label=${tr("So what do you do?")}><input maxlength="80" ...${bind("category_other")} /></${Field}>`}
    <${Field} label=${tr("What you do, and for whom")} hint=${tr("Two or three sentences: your work, your typical customer, what makes you different.")}>
      <textarea rows="4" dir="auto" ...${bind("about")}></textarea>
    </${Field}>
    <${Field} label=${tr("What customers can book")} hint=${tr("One per line, with length and price when you have one: \"Colour 2h ₪450\". Leave the price out and the agent won't quote one.")}>
      <textarea rows="5" dir="auto" ...${bind("services_text")}></textarea>
    </${Field}>
    <${Field} label=${tr("Opening hours")} hint=${tr("Free text, e.g. \"Sun-Thu 9:00-19:00, Fri 9:00-13:00, closed Saturday\". You can fix the exact hours in the next step.")}>
      <textarea rows="2" dir="auto" ...${bind("hours_text")}></textarea>
    </${Field}>
    <${Field} label=${tr("Policies")} hint=${tr("Cancellation, lateness, deposits, payment methods, anything the agent must say the same way every time.")}>
      <textarea rows="3" dir="auto" ...${bind("policies_text")}></textarea>
    </${Field}>
    <${Field} label=${tr("What customers ask you most")} hint=${tr("Parking, preparation before a treatment, what's included, prices, children, accessibility. These become the agent's knowledge base.")}>
      <textarea rows="4" dir="auto" ...${bind("faq_text")}></textarea>
    </${Field}>
    <${Field} label=${tr("When must a person take over?")} hint=${tr("One per line, e.g. \"anything about hair-loss treatments\", \"group events\".")}>
      <textarea rows="3" dir="auto" ...${bind("handoff_text")}></textarea>
    </${Field}>
    <div class="row">
      <${Field} label=${tr("Agent name")} hint=${tr("How it introduces itself to customers")}><input maxlength="60" ...${bind("agent_name")} /></${Field}>
      <${Field} label=${tr("Main language")}><input maxlength="40" ...${bind("language")} /></${Field}>
      <${Field} label=${tr("Tone of voice")} hint=${tr("e.g. warm and professional, short sentences")}><input maxlength="200" ...${bind("tone")} /></${Field}>
    </div>
    <div class="row">
      <${Field} label=${tr("Address and contact details")} hint=${tr("What the agent may share with customers")}><input maxlength="200" dir="auto" ...${bind("address")} /></${Field}>
    </div>
    ${data.source.error && html`<div class="callout warn small">${tr("Last time we couldn't read that page: {error}", { error: data.source.error })}</div>`}
    <div class="wizard-actions">
      <button class="btn primary" disabled=${busy}>${busy && !exploring ? tr("Writing your agent… this takes about half a minute") : tr("Draft my agent")}</button>
      <button type="button" class="btn" disabled=${busy} onClick=${saveForLater}>${tr("Save for later")}</button>
      <button type="button" class="btn ghost" disabled=${busy} onClick=${skip}>${tr("I'll set it up myself")}</button>
    </div>
    ${data.drafts_used > 0 && html`<p class="muted small">${tr("{n} AI drafts left", { n: data.drafts_left })}</p>`}
    <p class="muted small">${tr("Everything here can be changed later under AI agent.")}</p>
  </form>`;
}

const defaultHoursText = (weekly) =>
  DAYS.map((day, i) => {
    const text = weeklyHoursToText(weekly)[i];
    return text ? `${tr(day)} ${text}` : "";
  })
    .filter(Boolean)
    .join("\n");

/** Step 2: the draft, every field editable, nothing saved until the owner applies it. */
function ReviewStep({ t, data, draft, onBack, onRedraft, onApplied }) {
  const { values, bind, check, setValues } = useForm({ ...draft, enable_agent: true, apply_pack: true });
  const [packData] = useLoad(() => get(`${t.base}/setup/pack`), [t.id]);
  const [hours, setHours] = useState(() => weeklyHoursToText(Object.keys(draft.weekly_hours ?? {}).length ? draft.weekly_hours : data.prefill.weekly_hours));
  const [services, setServices] = useState(draft.services ?? []);
  const [knowledge, setKnowledge] = useState(draft.knowledge ?? []);
  const [busy, run] = useAction();

  const patchService = (i, patch) => setServices(services.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const patchKnowledge = (i, patch) => setKnowledge(knowledge.map((k, j) => (j === i ? { ...k, ...patch } : k)));

  const redraft = async () => {
    const res = await run(() => post(`${t.base}/setup/draft`, data.intake), tr("Drafted again"));
    setValues({ ...res.draft, enable_agent: values.enable_agent, apply_pack: values.apply_pack });
    setHours(weeklyHoursToText(res.draft.weekly_hours));
    setServices(res.draft.services);
    setKnowledge(res.draft.knowledge);
    onRedraft(res.draft, res.drafts_left);
  };

  const apply = async (e) => {
    e.preventDefault();
    const res = await run(
      () =>
        post(`${t.base}/setup/apply`, {
          ...values,
          weekly_hours: textToWeeklyHours(hours),
          services: services.map((s) => ({ ...s, duration_min: Number(s.duration_min), price: s.price === "" || s.price === null ? null : Number(s.price) })),
          knowledge,
        }),
      tr("Your agent is live"),
    );
    onApplied(res);
  };

  const open = values.open_questions ?? [];
  return html`<form onSubmit=${apply}>
    ${open.length > 0 &&
    html`<section class="card callout-card">
      <h2>${tr("Worth answering before you go live")}</h2>
      <p class="muted small">${tr("We left these out rather than guess. Anything in [square brackets] below is waiting for you.")}</p>
      <ul class="plain small">${open.map((q) => html`<li>• <span dir="auto">${q}</span></li>`)}</ul>
    </section>`}

    <section class="card">
      <h2>${tr("How the agent talks")}</h2>
      <div class="row">
        <${Field} label=${tr("Agent name")}><input required maxlength="60" ...${bind("agent_name")} /></${Field}>
        <${Field} label=${tr("Tone of voice")}><input required maxlength="200" ...${bind("tone")} /></${Field}>
        <${Field} label=${tr("Default language")}><input required maxlength="40" ...${bind("default_language")} /></${Field}>
      </div>
      <${Field} label=${tr("About the business")}><textarea rows="5" dir="auto" ...${bind("business_description")}></textarea></${Field}>
      <${Field} label=${tr("Sales script and policies")} hint=${tr("The rules the agent follows in every chat. Edit freely; this is the part you'll come back to most.")}>
        <textarea rows="8" dir="auto" ...${bind("instructions")}></textarea>
      </${Field}>
      <${Field} label=${tr("Also hand off to a person when…")} hint=${tr("One rule per line")}><textarea rows="3" dir="auto" ...${bind("handoff_rules")}></textarea></${Field}>
      <${Field} label=${tr("Address and contact details")}><input maxlength="200" dir="auto" ...${bind("address")} /></${Field}>
    </section>

    <section class="card">
      <h2>${tr("Opening hours")}</h2>
      <p class="muted small">${tr("The agent offers times only inside these hours. Format: 09:00-13:00, 16:00-19:00. Leave a day empty for closed.")}</p>
      <div class="hours-editor">
        ${DAYS.map((day, i) => html`<label><span>${tr(day)}</span><input dir="ltr" placeholder=${tr("closed")} value=${hours[i]} onInput=${(e) => setHours(hours.map((h, j) => (j === i ? e.target.value : h)))} /></label>`)}
      </div>
    </section>

    <section class="card table-wrap">
      <div class="section-head"><h2>${tr("Services")}</h2><span class="muted small">${tr("{n} selected", { n: services.filter((s) => s.include).length })}</span></div>
      ${services.length === 0
        ? html`<p class="muted">${tr("No services drafted. Add them under Services & staff after setup.")}</p>`
        : html`<table class="table">
            <thead><tr><th>${tr("Create")}</th><th>${tr("Name")}</th><th>${tr("Minutes")}</th><th>${tr("Price")}</th><th>${tr("Description")}</th></tr></thead>
            <tbody>${services.map((s, i) => html`<tr class=${s.include ? "" : "dim"}>
              <td><input type="checkbox" checked=${s.include} onChange=${(e) => patchService(i, { include: e.target.checked })} aria-label=${tr("Create this service")} /></td>
              <td><input dir="auto" value=${s.name} onInput=${(e) => patchService(i, { name: e.target.value })} /></td>
              <td><input type="number" min="5" max="720" value=${s.duration_min} onInput=${(e) => patchService(i, { duration_min: e.target.value })} /></td>
              <td><input type="number" min="0" step="1" placeholder=${tr("no price")} value=${s.price ?? ""} onInput=${(e) => patchService(i, { price: e.target.value })} /></td>
              <td><input dir="auto" value=${s.description} onInput=${(e) => patchService(i, { description: e.target.value })} /></td>
            </tr>`)}</tbody>
          </table>`}
    </section>

    <section class="card">
      <div class="section-head"><h2>${tr("Knowledge base")}</h2><span class="muted small">${tr("{n} selected", { n: knowledge.filter((k) => k.include).length })}</span></div>
      <p class="muted small">${tr("The agent answers only from these facts and hands off otherwise. Fill in anything in [square brackets].")}</p>
      ${knowledge.map((k, i) => html`<div class="draft-entry ${k.include ? "" : "dim"}">
        <label class="toggle"><input type="checkbox" checked=${k.include} onChange=${(e) => patchKnowledge(i, { include: e.target.checked })} />
          <input dir="auto" value=${k.title} onInput=${(e) => patchKnowledge(i, { title: e.target.value })} aria-label=${tr("Title")} /></label>
        <textarea rows="3" dir="auto" value=${k.content} onInput=${(e) => patchKnowledge(i, { content: e.target.value })}></textarea>
      </div>`)}
      ${knowledge.length === 0 && html`<p class="muted">${tr("Nothing drafted yet. You can add entries on the AI agent page.")}</p>`}
    </section>

    ${packData && html`<${StarterPackCard} pack=${packData.pack} check=${check} />`}

    <section class="card">
      <label class="toggle"><input type="checkbox" ...${check("enable_agent")} /> ${tr("Let the agent start answering customers now")}</label>
      <p class="muted small">${tr("Either way, nothing is sent until a customer writes to you. You can test it yourself in the chat on the AI agent page.")}</p>
      <div class="wizard-actions">
        <button class="btn primary" disabled=${busy}>${busy ? tr("Saving…") : tr("Save and finish setup")}</button>
        <button type="button" class="btn" disabled=${busy} onClick=${onBack}>${tr("Back to my answers")}</button>
        ${data.drafts_left > 0 && html`<button type="button" class="btn ghost" disabled=${busy} onClick=${redraft}>${tr("Draft again ({n} left)", { n: data.drafts_left })}</button>`}
      </div>
    </section>
  </form>`;
}

/**
 * The industry starter pack (src/onboarding/packs.ts): what gets switched on with the draft, and
 * the WhatsApp template wording to submit to Meta, ready to copy.
 */
function StarterPackCard({ pack, check }) {
  const copy = (text) => navigator.clipboard.writeText(text).then(() => toast(tr("Copied")));
  return html`<section class="card">
    <h2>${tr("Starter pack for your kind of business")}</h2>
    <label class="toggle"><input type="checkbox" ...${check("apply_pack")} /> ${tr("Switch these on with the draft")}</label>
    <ul class="plain small">
      ${pack.rebook_after_days && html`<li>• ${tr("Invite customers to book again {n} days after a visit (you can change it per service).", { n: pack.rebook_after_days })}</li>`}
      <li>• ${tr("Ask every customer for a 0 to 10 score after a visit; a low score brings in your team.")}</li>
      ${pack.segments.length > 0 && html`<li>• ${tr("Ready-made segments for campaigns: {names}.", { names: pack.segments.map((x) => tr(x.name)).join(", ") })}</li>`}
    </ul>
    <details>
      <summary class="small">${tr("WhatsApp templates to submit in WhatsApp Manager")}</summary>
      <p class="muted small">${tr("Messages to customers who haven't written in 24 hours need a template Meta has approved. Create these under WhatsApp Manager → Message templates, then put each name in its field on the AI agent page.")}</p>
      ${pack.templates.map((x) => html`<div class="draft-entry">
        <div class="section-head"><strong dir="ltr">${x.name}</strong><span class="muted small">${x.category.toLowerCase()} · ${tr(x.purpose)}</span></div>
        <pre class="code" dir="auto">${x.he}</pre>
        <div class="form-actions"><button type="button" class="btn small" onClick=${() => copy(x.he)}>${tr("Copy Hebrew")}</button><button type="button" class="btn small ghost" onClick=${() => copy(x.en)}>${tr("Copy English")}</button></div>
      </div>`)}
    </details>
  </section>`;
}

function DoneStep({ siteRequested }) {
  const next = [
    ...(siteRequested ? [{ href: "/website", label: tr("Build your website"), hint: tr("We write it from what you just told us. You pick the address and the look, then publish.") }] : []),
    { href: "/agent", label: tr("Test your agent in the chat"), hint: tr("Write to it as a customer would. Bookings you make there are real.") },
    { href: "/channels", label: tr("Connect your WhatsApp number"), hint: tr("Until then, the agent answers on your website chat.") },
    { href: "/settings/integrations", label: tr("Connect your Google or Outlook calendar"), hint: tr("Busy times block booking, and new appointments land in your calendar.") },
    { href: "/services", label: tr("Check services, staff and prices"), hint: tr("Everything we created is editable there.") },
    { href: "/contacts?import=1", label: tr("Bring in your existing customers"), hint: tr("Upload a spreadsheet of past customers so the agent knows them. Nothing is sent to them.") },
  ];
  return html`<section class="card setup-done">
    <${Logo} />
    <h2>${tr("Your agent is set up")}</h2>
    <p class="muted">${tr("It has your description, your rules, your services and your opening hours. Here's what to do next.")}</p>
    <ol class="plain">${next.map((n) => html`<li><a href=${n.href}><strong>${n.label}</strong></a><div class="muted small">${n.hint}</div></li>`)}</ol>
  </section>`;
}
